import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PropertyFilters } from '@/types/property';

interface NeighborhoodCount {
  neighborhood: string;
  city: string;
  count: number;
}

/**
 * Returns neighborhood counts filtered by the currently active filters
 * (excluding neighborhood itself). This ensures dropdown counts reflect
 * the actual number of matching properties when other filters are active.
 */
export function useFilteredNeighborhoodCounts(filters: PropertyFilters, enabled = true) {
  const hasActiveFilters = !!(
    filters.propertyType ||
    filters.minPrice ||
    filters.maxPrice ||
    filters.minBedrooms ||
    filters.minBathrooms ||
    filters.minSqft ||
    filters.maxSqft
  );

  return useQuery({
    queryKey: [
      'filtered-neighborhood-counts',
      filters.city,
      filters.propertyType,
      filters.minPrice,
      filters.maxPrice,
      filters.minBedrooms,
      filters.minBathrooms,
      filters.minSqft,
      filters.maxSqft,
    ],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_filtered_neighborhood_counts', {
        p_city: filters.city || null,
        p_property_type: filters.propertyType || null,
        p_min_price: filters.minPrice || null,
        p_max_price: filters.maxPrice || null,
        p_min_bedrooms: filters.minBedrooms || null,
        p_min_bathrooms: filters.minBathrooms || null,
        p_min_sqft: filters.minSqft || null,
        p_max_sqft: filters.maxSqft || null,
      });

      if (error) throw error;

      // Build a lookup map: "City - Neighborhood" -> count
      const countMap = new Map<string, number>();
      if (Array.isArray(data)) {
        (data as unknown as NeighborhoodCount[]).forEach((item) => {
          const key = `${item.city} - ${item.neighborhood}`;
          countMap.set(key, item.count);
        });
      }
      return countMap;
    },
    enabled: enabled && hasActiveFilters,
    staleTime: 1000 * 60 * 2,
  });
}
